import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useAdPlacement } from '../../../advertisement/composables/use-ad-placement'
import { useContentBlockStore } from '../../../content-blocks/stores/content-block-store'

import type { TeaserItem, TeaserOrNativeTeaser } from '../../ui/teaser/teaser-group-constants'

export function useNativeTeasers (options: {
  currentContentBlockId: string,
  teasers: TeaserItem[]
}) {
  const { currentContentBlockId, teasers } = options

  const adPlacement = useAdPlacement()
  const allQuadrupleTeasersInPage = useAllQuadrupleTeasersInPage()
  const allRelatedArticleBlocksInPage = useAllRelatedArticleBlocksInPage()

  return computed(() => {
    const howManyTeasersToReplaceQuadruple = adPlacement.value?.nativeTeasersPerQuadrupleTeaser ?? 0
    const howManyTeasersToReplaceForRelatedArticles = adPlacement.value?.nativeTeasersPerRelatedBlock ?? 0

    // If no teasers need to be replaced, return the original teasers
    if (!howManyTeasersToReplaceQuadruple && !howManyTeasersToReplaceForRelatedArticles) {
      return teasers
    }

    const result: TeaserOrNativeTeaser[] = [...teasers]

    if (howManyTeasersToReplaceQuadruple) {
      // The case for quadruple teasers
      const currentQuadrupleTeaserIndex = allQuadrupleTeasersInPage.value.findIndex(block => block.id === currentContentBlockId)

      if (currentQuadrupleTeaserIndex !== -1) {
        // Replace the last howManyTeasersToReplaceQuadruple teasers with native teasers
        for (let i = 0; i < howManyTeasersToReplaceQuadruple && i < result.length; i++) {
          const indexToReplace = result.length - howManyTeasersToReplaceQuadruple + i
          if (indexToReplace < result.length) {
            result[indexToReplace] = {
              type: 'native',
              // Create a unique index by combining the quadruple teaser index and position within the teaser group
              nativeTeaserIndex: (currentQuadrupleTeaserIndex * howManyTeasersToReplaceQuadruple) + i,
              fallback: result[indexToReplace] as TeaserItem
            }
          }
        }
      }
    }

    if (howManyTeasersToReplaceForRelatedArticles) {
      // The case for related articles
      const currentRelatedArticleBlockIndex = allRelatedArticleBlocksInPage.value.findIndex(block => block.id === currentContentBlockId)

      if (currentRelatedArticleBlockIndex !== -1) {
        // Replace the last howManyTeasersToReplaceForRelatedArticles teasers with native teasers
        for (let i = 0; i < howManyTeasersToReplaceForRelatedArticles && i < result.length; i++) {
          const indexToReplace = result.length - howManyTeasersToReplaceForRelatedArticles + i
          if (indexToReplace < result.length) {
            result[indexToReplace] = {
              type: 'native',
              nativeTeaserIndex: (currentRelatedArticleBlockIndex * howManyTeasersToReplaceForRelatedArticles) + i,
              fallback: result[indexToReplace] as TeaserItem
            }
          }
        }
      }
    }

    return result
  })
}

// Returns all quadruple teasers in the page
function useAllQuadrupleTeasersInPage () {
  const contentBlockStore = useContentBlockStore()
  const { contentBlockList } = storeToRefs(contentBlockStore)

  return computed(() => {
    const allEnabledContentBlocks = contentBlockList.value.filter(block => block.enabled)
    const allTeaserGroups = allEnabledContentBlocks.filter(block => block.type === 'teaserGroup' || block.type === 'pageableTeaserGroup')
    return allTeaserGroups.filter(block => {
      const data = block.data.teaserGroup || block.data.pageableTeaserGroup
      return data.teaserGroupType === 'bx-teaser-container--one-fourth-width'
    })
  })
}

// Returns all related article blocks in the page
function useAllRelatedArticleBlocksInPage () {
  const contentBlockStore = useContentBlockStore()
  const { contentBlockList } = storeToRefs(contentBlockStore)

  return computed(() => {
    const allEnabledContentBlocks = contentBlockList.value.filter(block => block.enabled)
    return allEnabledContentBlocks.filter(block => block.type === '__injected_related__')
  })
}
